// SCR-STAFF-201 تب «منبع و صلاحیت» (AP-CAT-02): الزامات منبع با هشدار «در شعبه X قابل ارائه نیست» (US-CAT-011، AC-CAT-011.2) و
// صلاحیت ارائه‌دهنده صریح (نوع، تخصص، مدرک، فهرست صریح) یا ضمنی BR-CAT-012 با پیش‌نمایش ارائه‌دهندگان مجاز (US-CAT-012).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ServiceEligibilityView,
  type ServiceResourcesView,
  type ServiceView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ErrorState,
  faDigits,
  LoadingState,
  SelectRHF,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  catKeys,
  CREDENTIAL_TYPES,
  PROVIDER_TYPES,
  SavedNotice,
  useProviders,
  useResourceTags,
  useSpecialties,
} from './common';

const resourcesSchema = z.object({
  items: z.array(
    z.object({
      roomTagCodes: z.array(z.string()).min(1, { error: 'common:validation.required' }),
      count: z
        .string()
        .refine((value) => Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 10, {
          message: 'errors:validation.invalid',
        }),
      occupancyMinutes: z
        .string()
        .refine((value) => value.trim() === '' || (Number(value) >= 5 && Number(value) % 5 === 0), {
          message: 'errors:validation.invalid',
        }),
    }),
  ),
});
type ResourcesForm = z.input<typeof resourcesSchema>;

const eligibilitySchema = z.object({
  explicit: z.boolean(),
  providerTypes: z.array(z.string()),
  specialtyCodes: z.array(z.string()),
  credentialType: z.string(),
  providerIds: z.array(z.string()),
});
type EligibilityForm = z.input<typeof eligibilitySchema>;

/** انتخاب چندتایی کد با Autocomplete متصل به RHF. */
function MultiCodeField<T extends FieldValues>({
  control,
  name,
  labelKey,
  options,
  freeSolo = false,
  disabled,
  testId,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  labelKey: string;
  options: readonly { value: string; label: string }[];
  freeSolo?: boolean;
  disabled?: boolean;
  testId: string;
}) {
  const { t } = useTranslation('cat');
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Autocomplete
          multiple
          freeSolo={freeSolo}
          options={options.map((option) => option.value)}
          value={(field.value as string[] | undefined) ?? []}
          onChange={(_, value) => field.onChange(value)}
          getOptionLabel={(value) => options.find((option) => option.value === value)?.label ?? value}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(labelKey)}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': testId } }}
            />
          )}
        />
      )}
    />
  );
}

function ResourcesSection({ service, view }: { service: ServiceView; view: ServiceResourcesView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const tags = useResourceTags();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<ResourcesForm>(
    () => ({
      items: view.items.map((item) => ({
        roomTagCodes: [...item.roomTagCodes],
        count: String(item.count),
        occupancyMinutes:
          item.occupancyMinutes === null || item.occupancyMinutes === undefined
            ? ''
            : String(item.occupancyMinutes),
      })),
    }),
    [view],
  );
  const { control, handleSubmit, formState, setError } = useForm<ResourcesForm>({
    resolver: zodResolver(resourcesSchema),
    values,
  });
  const items = useFieldArray({ control, name: 'items' });
  const tagOptions = tags.tags.map((tag) => ({
    value: tag.code.toLowerCase(),
    label: tag.nameKey ? t(tag.nameKey) : (tag.name ?? tag.code),
  }));

  const save = useMutation({
    mutationFn: (form: ResourcesForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/resource-requirements', {
          params: { path: { id: service.id } },
          body: {
            items: form.items.map((item) => ({
              roomTagCodes: item.roomTagCodes,
              count: Number(item.count),
              occupancyMinutes: item.occupancyMinutes.trim() ? Number(item.occupancyMinutes) : null,
            })),
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (data) => {
      setSaved(true);
      queryClient.setQueryData(catKeys.requirement(session?.tenantId, service.id, 'resources'), data);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: [],
        codeFields: { 'PHX-CAT-0401': 'items.0.roomTagCodes' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const missing = view.branches.filter((branch) => !branch.deliverable);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">{t('cat:resources.title')}</Typography>
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() => items.append({ roomTagCodes: [], count: '1', occupancyMinutes: '' })}
              data-testid="resource-add"
            >
              {t('cat:resources.add')}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('cat:resources.help')}
          </Typography>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          {missing.map((branch) => (
            <Alert
              key={branch.branchId}
              severity="warning"
              data-testid={`resource-branch-missing-${branch.branchId}`}
            >
              {t('cat:resources.branch_missing', { branch: branch.branchName })}
            </Alert>
          ))}
          {view.branches.length > 0 && missing.length === 0 && (
            <Alert severity="success" data-testid="resource-branches-ok">
              {t('cat:resources.branches_ok', { count: faDigits(view.branches.length) })}
            </Alert>
          )}
          {items.fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('cat:resources.none')}
            </Typography>
          )}
          {items.fields.map((field, index) => (
            <Stack
              key={field.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems="flex-start"
              data-testid={`resource-${index}`}
            >
              <Box sx={{ flex: 2, width: '100%' }}>
                <MultiCodeField
                  control={control}
                  name={`items.${index}.roomTagCodes`}
                  labelKey="cat:resources.tags"
                  options={tagOptions}
                  freeSolo={!tags.enabled}
                  disabled={!write.canWrite}
                  testId={`resource-tags-${index}`}
                />
              </Box>
              <TextFieldRHF
                control={control}
                name={`items.${index}.count`}
                labelKey="cat:resources.count"
                type="number"
                disabled={!write.canWrite}
                slotProps={{ htmlInput: { min: 1, max: 10 } }}
              />
              <TextFieldRHF
                control={control}
                name={`items.${index}.occupancyMinutes`}
                labelKey="cat:resources.occupancy"
                helperKey="cat:resources.occupancy_help"
                type="number"
                disabled={!write.canWrite}
                slotProps={{ htmlInput: { min: 5, step: 5 } }}
              />
              <IconButton
                aria-label={t('cat:resources.remove')}
                disabled={!write.canWrite}
                onClick={() => items.remove(index)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="resources-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

function EligibilitySection({ service, view }: { service: ServiceView; view: ServiceEligibilityView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const providers = useProviders();
  const specialties = useSpecialties();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<EligibilityForm>(
    () => ({
      explicit: view.isExplicit,
      providerTypes: [...(view.rule?.providerTypes ?? [])],
      specialtyCodes: [...(view.rule?.specialtyCodes ?? [])],
      credentialType: view.rule?.credentialType ?? '',
      providerIds: [...(view.rule?.providerIds ?? [])],
    }),
    [view],
  );
  const { control, handleSubmit, formState, setError } = useForm<EligibilityForm>({
    resolver: zodResolver(eligibilitySchema),
    values,
  });
  const explicit = useWatch({ control, name: 'explicit' });

  const save = useMutation({
    mutationFn: (form: EligibilityForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/eligibility', {
          params: { path: { id: service.id } },
          body: {
            rule: form.explicit
              ? {
                  providerTypes: form.providerTypes,
                  specialtyCodes: form.specialtyCodes,
                  credentialType: form.credentialType || null,
                  providerIds: form.providerIds,
                }
              : null,
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (data) => {
      setSaved(true);
      queryClient.setQueryData(catKeys.requirement(session?.tenantId, service.id, 'eligibility'), data);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['providerTypes', 'specialtyCodes', 'credentialType', 'providerIds'],
        codeFields: { 'PHX-CAT-0302': 'providerIds', 'PHX-CAT-0205': 'specialtyCodes' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">{t('cat:eligibility.title')}</Typography>
          <SavedNotice show={saved && !formState.isDirty} />
          <CheckboxRHF
            control={control}
            name="explicit"
            labelKey="cat:eligibility.explicit"
            helperKey="cat:eligibility.implicit_help"
            disabled={!write.canWrite}
          />
          {explicit && (
            <>
              <MultiCodeField
                control={control}
                name="providerTypes"
                labelKey="cat:eligibility.provider_types"
                options={PROVIDER_TYPES.map((type) => ({
                  value: type,
                  label: t(`cat:eligibility.provider_type.${type}`),
                }))}
                disabled={!write.canWrite}
                testId="eligibility-types"
              />
              <MultiCodeField
                control={control}
                name="specialtyCodes"
                labelKey="cat:eligibility.specialties"
                options={specialties.options.map((option) => ({ value: option.key, label: option.name }))}
                disabled={!write.canWrite}
                testId="eligibility-specialties"
              />
              <SelectRHF
                control={control}
                name="credentialType"
                labelKey="cat:eligibility.credential"
                helperKey="cat:eligibility.credential_help"
                disabled={!write.canWrite}
                options={[
                  { value: '', labelKey: 'cat:eligibility.no_credential' },
                  ...CREDENTIAL_TYPES.map((type) => ({
                    value: type,
                    labelKey: `cat:eligibility.credentials.${type.replace(':', '_')}`,
                  })),
                ]}
              />
              <MultiCodeField
                control={control}
                name="providerIds"
                labelKey="cat:eligibility.providers"
                options={providers.options.map((option) => ({ value: option.id, label: option.name }))}
                disabled={!write.canWrite}
                testId="eligibility-providers"
              />
            </>
          )}
          <Typography variant="subtitle2">{t('cat:eligibility.preview')}</Typography>
          {view.eligible.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="eligibility-empty">
              {t('cat:eligibility.preview_empty')}
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="eligibility-preview">
              {view.eligible.map((provider) => (
                <Chip
                  key={provider.providerId}
                  label={
                    provider.warnings.length > 0
                      ? `${provider.displayName || providers.name(provider.providerId)} — ${provider.warnings.map((warning) => t(`cat:eligibility.warning.${warning}`)).join(t('cat:list_separator'))}`
                      : provider.displayName || providers.name(provider.providerId)
                  }
                  color={provider.warnings.length > 0 ? 'warning' : 'default'}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="eligibility-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

/** تب منبع و صلاحیت. */
export function RequirementsTab({ service }: { service: ServiceView }) {
  const { tenantApi, session } = useTenantSession();
  const resources = useQuery({
    queryKey: catKeys.requirement(session?.tenantId, service.id, 'resources'),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services/{id}/resource-requirements', {
          params: { path: { id: service.id } },
        }),
      ),
  });
  const eligibility = useQuery({
    queryKey: catKeys.requirement(session?.tenantId, service.id, 'eligibility'),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services/{id}/eligibility', { params: { path: { id: service.id } } }),
      ),
  });

  if (resources.isPending || eligibility.isPending) {
    return <LoadingState rows={3} />;
  }
  if (resources.error || eligibility.error) {
    return (
      <ErrorState
        error={resources.error ?? eligibility.error}
        onRetry={() => {
          void resources.refetch();
          void eligibility.refetch();
        }}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <ResourcesSection service={service} view={resources.data} />
      <EligibilitySection service={service} view={eligibility.data} />
    </Stack>
  );
}
